import {DeviceTypeSet} from "@/api";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
    TypographyProps,
} from "@mui/material";
import {NoValidSet} from "./common";
import {defineMessage, defineMessages, FormattedMessage, MessageDescriptor, useIntl} from "react-intl";
import {NumberField} from "@dvrt/graphql-widgets";
import {deviceTranslations, DeviceType} from "../devices/utils";
import {unlimitedToNumber} from "@/tools";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {alpha} from "@mui/system";
import {CSSProperties, useMemo} from "react";
import {MaybeUnlimitedNumber} from "@/components/MaybeUnlimitedNumber";

const translations = defineMessages({
    "headers.devices": {
        id: "device_type_set_allocator.headers.devices",
        defaultMessage: "Devices",
    },
    "headers.user": {
        id: "device_type_set_allocator.headers.user",
        defaultMessage: "User",
    },
    "headers.domain": {
        id: "device_type_set_allocator.headers.domain",
        defaultMessage: "Domain",
    },
    "headers.system": {
        id: "device_type_set_allocator.headers.system",
        defaultMessage: "System",
    },
    "headers.fromUserPackage": {
        id: "device_type_set_allocator.headers.fromUserPackage",
        defaultMessage: "From User Package",
    },
    "headers.extra": {
        id: "device_type_set_allocator.headers.extra",
        defaultMessage: "Extra",
    },
    "headers.available": {
        id: "device_type_set_allocator.headers.available",
        defaultMessage: "Available",
    },
    "headers.used": {
        id: "device_type_set_allocator.headers.used",
        defaultMessage: "Used",
    },
    "headers.allocated": {
        id: "device_type_set_allocator.headers.allocated",
        defaultMessage: "Allocated",
    },
    "headers.limit": {
        id: "device_type_set_allocator.headers.limit",
        defaultMessage: "Limit",
    },
    "footer.exampleNoChange": {
        id: "device_type_set_allocator.footer.exampleNoChange",
        defaultMessage: "Current value is 0 and will remain 0",
    },
    "footer.exampleChange": {
        id: "device_type_set_allocator.footer.exampleChange",
        defaultMessage: "The current value of 0 will change to 1",
    },
});

type RowUserLimit = {
    userPackage: number;
    extra: number;
};

type RowUserLimitComparison = {
    current?: RowUserLimit;
    form: RowUserLimit;
};

type RowDomainAllocation = {
    used: number;
    allocated: number;
};

type RowSystemAllocation = {
    totalAllocated: number;
    limit: number;
};

// FIXME: due to the limitations of how our NumberField works, their width has to be set specifically
// to achieve kind-of-good size, but this should not be the case ideally
const NUMBER_FIELDS_WIDTH = 100;

// NOTE: since the table has a lot of columns, and does not make sense to be chopped into multiple tables,
// it has to have a min width to avoid looking too squeezed and be incomprehensible
const TABLE_MIN_WIDTH = 1200;

/**
 * Allows defining ratios for columns to generate width percentages.
 * @see getCssSizesFromRatios for details.
 **/
type CssSizeRatios = {
    [key: string]: {
        /** Gets turned into a percentage 'width' field */
        ratio: number;

        /** CSS compliant */
        minWidth?: number;
    };
};

/**
 * Generate CSS size styles based on defined ratios. The width is calculated by dividing the ratio of each item
 * by the total ratio and multiplying by 100 to get a percentage. This allows for easy adjustments of column widths
 * by simply changing the ratios, without having to manually calculate percentages.
 *
 */
const getCssSizesFromRatios = <T extends CssSizeRatios>(ratios: T): {[K in keyof T]: CSSProperties} => {
    const total = Object.values(ratios).reduce((sum, val) => sum + val.ratio, 0);
    const percentages = Object.fromEntries(
        Object.entries(ratios).map(([key, val]) => [
            key,
            {
                width: total > 0 ? `${(val.ratio / total) * 100}%` : "0%",
                minWidth: val.minWidth,
            },
        ]),
    ) as {[K in keyof T]: CSSProperties};
    return percentages;
};

/**
 * Accurate and detailed representation of user, domain and system limits.
 */
type AllocationSummary = {
    user: RowUserLimitComparison;
    domain: RowDomainAllocation;
    system?: RowSystemAllocation;
};

/**
 * Categorized side effects of changing the user's extra allocation.
 * @see getChangeSideEffects for details.
 **/
type ChangeSideEffects = {
    domain: RowDomainAllocation;
    system?: RowSystemAllocation;
};

/** Return the domain used without the current user's extra allocation for more accurate side effect calculations. */
const getDomainUsedWithoutCurrentUserAllocation = (allocation: AllocationSummary): number => {
    return allocation.domain.used - (allocation.user.current?.extra ?? 0);
};

/** Calculate the max value for the user's extra allocation. */
const getExtraMaxForUser = (allocation: AllocationSummary): number => {
    if (!allocation.system) return allocation.domain.allocated - getDomainUsedWithoutCurrentUserAllocation(allocation);

    const systemRemaining = unlimitedToNumber(allocation.system.limit) - allocation.system.totalAllocated;
    const domainRemaining = allocation.domain.allocated - getDomainUsedWithoutCurrentUserAllocation(allocation);

    return systemRemaining + domainRemaining;
};

/** Calculate how the domain and system related values would change if the user allocation is changed. */
const getChangeSideEffects = (allocation: AllocationSummary): ChangeSideEffects => {
    const newDomainUsed = allocation.domain.used + allocation.user.form.extra - (allocation.user.current?.extra ?? 0);
    const newDomainAllocated = Math.max(allocation.domain.allocated, newDomainUsed);
    const newDomainAllocatedDiff = newDomainAllocated - allocation.domain.allocated;

    const newSystemTotalAllocated = allocation.system
        ? allocation.system.totalAllocated + newDomainAllocatedDiff
        : undefined;

    return {
        domain: {
            used: newDomainUsed,
            allocated: newDomainAllocated,
        },
        system: allocation.system
            ? {
                  totalAllocated: newSystemTotalAllocated,
                  limit: allocation.system.limit,
              }
            : undefined,
    };
};

/**
 * Table does not support flexbox, and setting widths in pixels would make the table not responsive at all,
 * so this looks like the sweet spot between responsiveness and ease of styling for this use case.
 */
const columnWidthPercentages = getCssSizesFromRatios({
    devices: {ratio: 4, minWidth: 100},
    fromUserPackage: {ratio: 1},
    extra: {ratio: 10, minWidth: NUMBER_FIELDS_WIDTH * 2},
    available: {ratio: 1},
    domainUsed: {ratio: 1},
    domainAllocated: {ratio: 1},
    systemUsed: {ratio: 1},
    systemLimit: {ratio: 1},
});

type DeviceTypeSetAllocatorProps = {
    /** User current and form limits for each device type set */
    userLimits: Record<string, {current?: number; form: number}>;

    /** Quota for each device type set the current and form user package has */
    userPackageDeviceTypeSets: {
        current?: Record<string, number>;
        form: Record<string, number>;
    };

    /** Domain limits for each device type set */
    domainLimits?: Record<string, RowDomainAllocation>;

    /** System limits for each device type set */
    systemLimits?: Record<string, RowSystemAllocation>;

    /** All device type sets to get their device types and display them */
    deviceTypeSets: DeviceTypeSet[];

    disabled: boolean;
    updateValue: (name: string, val: number) => void;
};

const DeviceTypeSetAllocator = ({
    userLimits,
    userPackageDeviceTypeSets,
    systemLimits,
    domainLimits,
    deviceTypeSets,
    updateValue,
    disabled,
}: DeviceTypeSetAllocatorProps) => {
    const intl = useIntl();
    const showSystemColumn = systemLimits != null;

    console.log("Rendering DeviceTypeSetAllocator with props", {
        userLimits,
        userPackageDeviceTypeSets,
        systemLimits,
        domainLimits,
        deviceTypeSets,
    });

    if (deviceTypeSets.length === 0)
        return (
            <NoValidSet
                msgDesc={defineMessage({
                    id: "no_valid_device_type_set",
                    defaultMessage: "There is no valid device type set",
                })}
            />
        );
    // NOTE: all hooks are safe afterwards, because the length of the device type sets is guaranteed to be the same

    // We want to display the device types a device type set provides.
    // For that we need to translate each device type the set contains, and later join them with commas to display
    // in a single cell.
    // It is probably not a computationally heavy task to do, but it does not make sense to do it on every render,
    // so the overhead of useMemo is not a deal breaker.
    const transatedDeviceTypeSets: Record<string, string[]> = useMemo(
        () =>
            Object.fromEntries(
                deviceTypeSets.map((dts) => [
                    dts.name,
                    dts.devices.map((name) => intl.formatMessage(deviceTranslations[name as DeviceType])),
                ]),
            ),
        [deviceTypeSets, intl],
    );

    return (
        <TableContainer component={Paper}>
            <Table sx={{tableLayout: "fixed", minWidth: TABLE_MIN_WIDTH}} size="medium">
                <Headers showSystemColumn={showSystemColumn} />
                <TableBody>
                    {deviceTypeSets.map((dts) => {
                        const deviceTypeName = transatedDeviceTypeSets[dts.name].join(", ");

                        const userAllocation: RowUserLimitComparison = {
                            form: {
                                userPackage: userPackageDeviceTypeSets.form[dts.name],
                                extra: userLimits[dts.name].form,
                            },
                        };
                        if (userLimits[dts.name].current != null) {
                            userAllocation.current = {
                                userPackage: userPackageDeviceTypeSets.current?.[dts.name] ?? 0,
                                extra: userLimits[dts.name].current,
                            };
                        }

                        const domainAllocation = domainLimits[dts.name];
                        const systemAllocation = systemLimits?.[dts.name];

                        return (
                            <DeviceTypeSetRow
                                key={dts.name}
                                displayName={deviceTypeName}
                                name={dts.name}
                                userAllocation={userAllocation}
                                domainAllocation={domainAllocation}
                                systemAllocation={systemAllocation}
                                updateValue={updateValue}
                                disabled={disabled}
                            />
                        );
                    })}
                </TableBody>
                <Footer showSystemColumn={showSystemColumn} />
            </Table>
        </TableContainer>
    );
};

const Headers: React.FC<{
    showSystemColumn: boolean;
}> = ({showSystemColumn}) => (
    <TableHead>
        <TableRow>
            <TableCell
                rowSpan={2}
                sx={(theme) => ({
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    ...columnWidthPercentages.devices,
                })}
            >
                <FormattedTypography message={translations["headers.devices"]} />
            </TableCell>
            <TableCell colSpan={3} align="center" sx={(theme) => ({bgcolor: alpha(theme.palette.primary.main, 0.2)})}>
                <FormattedTypography message={translations["headers.user"]} />
            </TableCell>
            <TableCell
                colSpan={2}
                align="center"
                sx={(theme) => ({
                    bgcolor: alpha(theme.palette.primary.main, 0.3),
                })}
            >
                <FormattedTypography message={translations["headers.domain"]} />
            </TableCell>
            {showSystemColumn && (
                <TableCell
                    colSpan={2}
                    align="center"
                    sx={(theme) => ({
                        bgcolor: alpha(theme.palette.primary.main, 0.4),
                    })}
                >
                    <FormattedTypography message={translations["headers.system"]} />
                </TableCell>
            )}
        </TableRow>
        <TableRow>
            <TableCell align="center" sx={columnWidthPercentages.fromUserPackage}>
                <FormattedTypography message={translations["headers.fromUserPackage"]} />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.extra}>
                <FormattedTypography message={translations["headers.extra"]} />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.available}>
                <FormattedTypography message={translations["headers.available"]} />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.domainUsed}>
                <FormattedTypography message={translations["headers.used"]} />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.domainAllocated}>
                <FormattedTypography message={translations["headers.allocated"]} />
            </TableCell>
            {showSystemColumn && (
                <>
                    <TableCell align="center" sx={columnWidthPercentages.systemUsed}>
                        <FormattedTypography message={translations["headers.used"]} />
                    </TableCell>
                    <TableCell align="center" sx={columnWidthPercentages.systemLimit}>
                        <FormattedTypography message={translations["headers.limit"]} />
                    </TableCell>
                </>
            )}
        </TableRow>
    </TableHead>
);

const Footer: React.FC<{showSystemColumn: boolean}> = ({showSystemColumn}) => (
    <TableFooter>
        <TableRow>
            <TableCell colSpan={showSystemColumn ? 8 : 6}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        opacity: 0.8,
                    }}
                >
                    <FooterExample current={0} changed={0} message={translations["footer.exampleNoChange"]} />
                    <FooterExample current={0} changed={1} message={translations["footer.exampleChange"]} />
                </Box>
            </TableCell>
        </TableRow>
    </TableFooter>
);

const FooterExample: React.FC<{
    current: number;
    changed: number;
    message: MessageDescriptor;
}> = ({current, changed, message}) => (
    <Box display="flex" gap={1} alignItems="center">
        <DeviceTypeSetRowNumber current={current} changed={changed} noColor />
        <span>—</span>
        <FormattedTypography message={message} />
    </Box>
);

type DeviceTypeSetRowProps = {
    name: string;
    displayName: string;
    userAllocation: RowUserLimitComparison;
    domainAllocation: RowDomainAllocation;
    systemAllocation?: RowSystemAllocation;

    disabled?: boolean;
    updateValue: (name: string, val: number) => void;
};

const DeviceTypeSetRow: React.FC<DeviceTypeSetRowProps> = ({
    name,
    displayName,
    userAllocation,
    domainAllocation,
    systemAllocation,
    disabled,
    updateValue,
}) => {
    const showSystemColumn = systemAllocation != null;

    const extraMax = getExtraMaxForUser({user: userAllocation, domain: domainAllocation, system: systemAllocation});
    const changeSideEffects = getChangeSideEffects({
        user: userAllocation,
        domain: domainAllocation,
        system: systemAllocation,
    });

    return (
        <TableRow>
            <TableCell sx={columnWidthPercentages.devices}>{displayName}</TableCell>
            <TableCell sx={columnWidthPercentages.fromUserPackage} align="center">
                <DeviceTypeSetRowNumber
                    current={userAllocation.current?.userPackage}
                    changed={userAllocation.form.userPackage}
                />
            </TableCell>
            <TableCell sx={columnWidthPercentages.extra}>
                <Box display="flex" gap={1} alignItems="center" justifyContent="center">
                    {userAllocation.current && (
                        <>
                            <Typography>{userAllocation.current?.extra}</Typography>
                            <FontAwesomeIcon icon="arrow-right-long" />
                        </>
                    )}
                    <Box>
                        <NumberField
                            value={userAllocation.form.extra}
                            update={(newValue) => updateValue(name, newValue)}
                            fullWidth={false}
                            min={0}
                            max={extraMax}
                            disabled={disabled || extraMax <= 0}
                            style={{
                                maxWidth: NUMBER_FIELDS_WIDTH,
                            }}
                        />
                    </Box>
                </Box>
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.available}>
                <DeviceTypeSetRowNumber
                    current={
                        userAllocation.current
                            ? userAllocation.current.userPackage + userAllocation.current.extra
                            : undefined
                    }
                    changed={userAllocation.form.userPackage + userAllocation.form.extra}
                />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.domainUsed}>
                <DeviceTypeSetRowNumber current={domainAllocation.used} changed={changeSideEffects.domain.used} />
            </TableCell>
            <TableCell align="center" sx={columnWidthPercentages.domainAllocated}>
                <DeviceTypeSetRowNumber
                    current={domainAllocation.allocated}
                    changed={changeSideEffects.domain.allocated}
                />
            </TableCell>
            {showSystemColumn && (
                <>
                    <TableCell align="center" sx={columnWidthPercentages.systemUsed}>
                        <DeviceTypeSetRowNumber
                            current={systemAllocation?.totalAllocated}
                            changed={changeSideEffects.system?.totalAllocated}
                        />
                    </TableCell>
                    <TableCell align="center" sx={columnWidthPercentages.systemLimit}>
                        <DeviceTypeSetRowNumber
                            current={systemAllocation?.limit}
                            changed={changeSideEffects.system?.limit}
                        />
                    </TableCell>
                </>
            )}
        </TableRow>
    );
};

const DeviceTypeSetRowNumber: React.FC<{
    current?: number;
    changed: number;
    noColor?: boolean;
}> = ({current, changed, noColor}) => {
    if (current === undefined || current === changed)
        return (
            <Typography>
                <MaybeUnlimitedNumber value={changed} unlimitedColor={noColor ? undefined : "primary"} />
            </Typography>
        );

    return (
        <Box display="flex" gap={1} alignItems="center" justifyContent="center">
            <Typography>
                <MaybeUnlimitedNumber value={current} unlimitedColor={noColor ? undefined : "primary"} />
            </Typography>
            <FontAwesomeIcon icon="arrow-right-long" />
            <Typography
                sx={(theme) => ({
                    color: noColor
                        ? undefined
                        : changed > current
                          ? theme.palette.success.main
                          : theme.palette.error.main,
                })}
            >
                <MaybeUnlimitedNumber value={changed} unlimitedColor={noColor ? undefined : "primary"} />
            </Typography>
        </Box>
    );
};

const FormattedTypography: React.FC<
    TypographyProps & {message: MessageDescriptor; messageValues?: Record<string, any>}
> = ({message, messageValues, ...typographyProps}) => (
    <Typography {...typographyProps}>
        <FormattedMessage {...message} values={messageValues} />
    </Typography>
);

export default DeviceTypeSetAllocator;
